import {useMemo, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {ChevronGlyph} from '@/components/ui/icons'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Button} from '@/components/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {
  useOrganigrama,
  useMoveEmployeeArea,
  useSetEmployeePosition,
  organigramaExportUrl,
  type Organigrama,
} from '@/hooks/empresa/organizacion/use-organigrama'
import {downloadBlob} from '@/lib/api'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useTags, type Tag as TagRecord} from '@/hooks/empresa/organizacion/use-tags'
import {SegmentedControl} from '@/components/segmented-control'
import {TreeNode} from '@/components/perfil/organigrama-tree'
import {OrganigramaDiagrama} from '@/components/perfil/organigrama-diagrama'
import {normalize} from '@/lib/utils'

type ViewKey = 'estructura' | 'reporte'
type ReporteModo = 'lista' | 'diagrama'
type Persona = Organigrama['estructura'][number]['personas_sin_area'][number]
type Reporte = Organigrama['reporte'][number]

// Submits the name: the employee PATCH find-or-creates it on save.
function TagAssignCombobox({
  testId,
  ariaLabel,
  available,
  value,
  placeholder,
  disabled,
  onChange,
  onClear,
}: {
  testId: string
  ariaLabel: string
  available: TagRecord[]
  value: string | null
  placeholder: string
  disabled?: boolean
  onChange: (name: string) => void
  onClear: () => void
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const normalizedQuery = normalize(query)

  const matches = useMemo(() => {
    if (!normalizedQuery) return available.slice(0, 6)
    return available.filter((tag) => normalize(tag.name).includes(normalizedQuery)).slice(0, 6)
  }, [available, normalizedQuery])

  const exactExists = useMemo(
    () => available.some((tag) => normalize(tag.name) === normalizedQuery),
    [available, normalizedQuery]
  )
  const canCreate = !!normalizedQuery && !exactExists

  function pick(name: string) {
    onChange(name)
    setOpen(false)
    setQuery('')
  }

  function handleOpenChange(next: boolean) {
    if (disabled) return
    setOpen(next)
    if (!next) setQuery('')
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="flex h-7 max-w-40 items-center gap-1 rounded-md border border-input bg-transparent px-1.5 text-xs text-foreground disabled:opacity-60">
          <span className="min-w-0 truncate">{value ?? placeholder}</span>
          <ChevronGlyph size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-64 p-0" data-testid={testId}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('tags.combobox.placeholder')}
          data-testid={`${testId}-input`}
          className="w-full border-b border-border bg-transparent px-3 py-2 text-sm outline-none"
        />
        <ul className="max-h-56 overflow-auto p-1">
          <li>
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={() => {
                onClear()
                setOpen(false)
                setQuery('')
              }}
              className="flex w-full items-center px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-secondary">
              {t('business.organigrama.sinAsignar')}
            </button>
          </li>
          {matches.map((tagOption) => (
            <li key={tagOption.id}>
              <button
                type="button"
                data-testid={`${testId}-option`}
                onClick={() => pick(tagOption.name)}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-secondary">
                <span className="min-w-0 truncate">{tagOption.name}</span>
              </button>
            </li>
          ))}
          {canCreate && (
            <li className="border-t border-border">
              <button
                type="button"
                data-testid={`${testId}-create`}
                onClick={() => pick(query)}
                className="flex w-full items-center px-2 py-1.5 text-left text-sm text-primary hover:bg-secondary">
                <span className="min-w-0 truncate">{t('tags.combobox.create', {name: query.trim()})}</span>
              </button>
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

// Replaces only this node's área: a person can hold several.
function AreaAssignCombobox({
  businessId,
  employeeId,
  currentAreaId,
  currentAreaName,
  areaOptions,
  roster,
}: {
  businessId: string
  employeeId: string
  currentAreaId: string | null
  currentAreaName: string | null
  areaOptions: TagRecord[]
  roster: Employee[] | undefined
}) {
  const {t} = useTranslation()
  const move = useMoveEmployeeArea(businessId)
  const person = roster?.find((e) => e.id === employeeId)

  function replace(nextAreaName: string | null) {
    if (!person) return
    const otherAreaNames = person.areas
      .filter((a) => a.id !== currentAreaId && a.name !== currentAreaName)
      .map((a) => a.name)
    const areaNames = nextAreaName ? [...otherAreaNames, nextAreaName] : otherAreaNames
    move.mutate({employeeId, areaNames})
  }

  return (
    <TagAssignCombobox
      testId={`organigrama-move-area-${employeeId}`}
      ariaLabel={t('business.organigrama.moverArea')}
      available={areaOptions}
      value={currentAreaName}
      placeholder={t('business.organigrama.sinArea')}
      disabled={move.isPending || !person}
      onChange={replace}
      onClear={() => replace(null)}
    />
  )
}

// One cargo per node: assigning replaces the whole positions list.
function PositionAssignCombobox({
  businessId,
  employeeId,
  currentPositionName,
  positionOptions,
  roster,
}: {
  businessId: string
  employeeId: string
  currentPositionName: string | null
  positionOptions: TagRecord[]
  roster: Employee[] | undefined
}) {
  const {t} = useTranslation()
  const setPosition = useSetEmployeePosition(businessId)
  const person = roster?.find((e) => e.id === employeeId)

  function replace(nextPositionName: string | null) {
    if (!person) return
    setPosition.mutate({employeeId, positionNames: nextPositionName ? [nextPositionName] : []})
  }

  return (
    <TagAssignCombobox
      testId={`organigrama-move-position-${employeeId}`}
      ariaLabel={t('business.organigrama.asignarCargo')}
      available={positionOptions}
      value={currentPositionName}
      placeholder={t('business.organigrama.sinCargo')}
      disabled={setPosition.isPending || !person}
      onChange={replace}
      onClear={() => replace(null)}
    />
  )
}

function PersonaMeta({cargo, area, fuente}: {cargo: string | null; area: string | null; fuente?: ReactNode}) {
  return (
    <span className="ml-1.5 flex flex-wrap items-center gap-1.5">
      {cargo && <Tag tone="neutral">{cargo}</Tag>}
      {area && <Tag tone="neutral">{area}</Tag>}
      {fuente && <span className="text-xs text-muted-foreground/60">{fuente}</span>}
    </span>
  )
}

function PersonaNode({
  persona,
  isResponsable,
  testId,
  moveControl,
}: {
  persona: Persona
  isResponsable: boolean
  testId: string
  moveControl?: ReactNode
}) {
  const {t} = useTranslation()
  return (
    <TreeNode
      depth={2}
      testId={testId}
      label={persona.nombre_completo}
      meta={
        <span className="ml-1.5 flex flex-wrap items-center gap-1.5">
          {persona.cargo && <Tag tone="neutral">{persona.cargo}</Tag>}
          {/* `neutral`, no `green`: ser responsable es una identidad, no un estado (U2). */}
          {isResponsable && <Tag tone="neutral">{t('business.organigrama.responsable')}</Tag>}
          {moveControl}
        </span>
      }
    />
  )
}

function useOrganigramaEditCatalogs(businessId: string, canEdit: boolean) {
  const {data: roster} = useAllEmployees(canEdit ? businessId : undefined)
  const {data: areaTags} = useTags(businessId, 'areas', canEdit)
  const {data: positionTags} = useTags(businessId, 'positions', canEdit)
  return {
    roster,
    areaOptions: areaTags?.items ?? [],
    positionOptions: positionTags?.items ?? [],
  }
}

function EstructuraMoveControls({
  businessId,
  employeeId,
  currentAreaId,
  areaOptions,
  positionOptions,
  roster,
}: {
  businessId: string
  employeeId: string
  currentAreaId: string | null
  areaOptions: TagRecord[]
  positionOptions: TagRecord[]
  roster: Employee[] | undefined
}) {
  const person = roster?.find((e) => e.id === employeeId)
  const currentAreaName = currentAreaId ? (person?.areas.find((a) => a.id === currentAreaId)?.name ?? null) : null
  const currentPositionName = person?.positions[0]?.name ?? null
  return (
    <>
      <PositionAssignCombobox
        businessId={businessId}
        employeeId={employeeId}
        currentPositionName={currentPositionName}
        positionOptions={positionOptions}
        roster={roster}
      />
      <AreaAssignCombobox
        businessId={businessId}
        employeeId={employeeId}
        currentAreaId={currentAreaId}
        currentAreaName={currentAreaName}
        areaOptions={areaOptions}
        roster={roster}
      />
    </>
  )
}

function EstructuraView({
  businessId,
  estructura,
  canEdit,
}: {
  businessId: string
  estructura: Organigrama['estructura']
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const {roster, areaOptions, positionOptions} = useOrganigramaEditCatalogs(businessId, canEdit)

  if (estructura.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t('business.organigrama.empty')}</p>
  }
  return (
    <div data-testid="organigrama-estructura">
      {estructura.map((sede) => (
        <TreeNode
          key={sede.sede_id ?? 'sin-sede'}
          depth={0}
          label={sede.name}
          testId={`organigrama-sede-${sede.sede_id ?? 'none'}`}>
          {sede.areas.map((area) => (
            <TreeNode key={area.area_id} depth={1} label={area.name} testId={`organigrama-area-${area.area_id}`}>
              {area.personas.map((persona) => (
                <PersonaNode
                  key={persona.employee_id}
                  persona={persona}
                  isResponsable={persona.employee_id === area.responsable_employee_id}
                  testId={`organigrama-persona-${persona.employee_id}`}
                  moveControl={
                    canEdit && (
                      <EstructuraMoveControls
                        businessId={businessId}
                        employeeId={persona.employee_id}
                        currentAreaId={area.area_id}
                        areaOptions={areaOptions}
                        positionOptions={positionOptions}
                        roster={roster}
                      />
                    )
                  }
                />
              ))}
            </TreeNode>
          ))}
          {sede.personas_sin_area.length > 0 && (
            <TreeNode
              depth={1}
              label={t('business.organigrama.sinArea')}
              testId={`organigrama-sin-area-${sede.sede_id ?? 'none'}`}>
              {sede.personas_sin_area.map((persona) => (
                <PersonaNode
                  key={persona.employee_id}
                  persona={persona}
                  isResponsable={false}
                  testId={`organigrama-persona-${persona.employee_id}`}
                  moveControl={
                    canEdit && (
                      <EstructuraMoveControls
                        businessId={businessId}
                        employeeId={persona.employee_id}
                        currentAreaId={null}
                        areaOptions={areaOptions}
                        positionOptions={positionOptions}
                        roster={roster}
                      />
                    )
                  }
                />
              ))}
            </TreeNode>
          )}
        </TreeNode>
      ))}
    </div>
  )
}

const FUENTE_LABEL_KEY: Record<Reporte['fuente'], string> = {
  jefe_directo: 'business.organigrama.fuenteJefeDirecto',
  responsable_area: 'business.organigrama.fuenteResponsableArea',
  owner_fallback: 'business.organigrama.fuenteOwner',
}

function ReporteView({
  businessId,
  reporte,
  canEdit,
}: {
  businessId: string
  reporte: Organigrama['reporte']
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const {roster, areaOptions, positionOptions} = useOrganigramaEditCatalogs(businessId, canEdit)

  if (reporte.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t('business.organigrama.empty')}</p>
  }
  const byJefe = new Map<string, Reporte[]>()
  for (const row of reporte) {
    const key = row.jefe_employee_id ?? '__sin_jefe__'
    const bucket = byJefe.get(key) ?? []
    bucket.push(row)
    byJefe.set(key, bucket)
  }
  // The owner fallback in core/jerarquia.py makes the only active owner their own jefe.
  const roots = reporte.filter(
    (row) =>
      row.jefe_employee_id === null ||
      row.jefe_employee_id === row.employee_id ||
      !reporte.some((r) => r.employee_id === row.jefe_employee_id)
  )

  function renderRowMeta(row: Reporte) {
    const person = roster?.find((e) => e.id === row.employee_id)
    return (
      <span className="ml-1.5 flex flex-wrap items-center gap-1.5">
        <PersonaMeta
          cargo={row.cargo}
          area={row.area}
          fuente={
            <>
              {t(FUENTE_LABEL_KEY[row.fuente])}
              {row.ambiguo && (
                <Tag tone="amber" className="ml-1.5">
                  {t('business.organigrama.ambiguo')}
                </Tag>
              )}
            </>
          }
        />
        {canEdit && (
          <>
            <PositionAssignCombobox
              businessId={businessId}
              employeeId={row.employee_id}
              currentPositionName={person?.positions[0]?.name ?? null}
              positionOptions={positionOptions}
              roster={roster}
            />
            <AreaAssignCombobox
              businessId={businessId}
              employeeId={row.employee_id}
              currentAreaId={person?.areas.find((a) => a.name === row.area)?.id ?? null}
              currentAreaName={row.area}
              areaOptions={areaOptions}
              roster={roster}
            />
          </>
        )}
      </span>
    )
  }

  function renderRow(row: Reporte, depth: number, seen: Set<string>) {
    const children = seen.has(row.employee_id)
      ? []
      : (byJefe.get(row.employee_id) ?? []).filter((child) => child.employee_id !== row.employee_id)
    const nextSeen = new Set(seen).add(row.employee_id)
    return (
      <TreeNode
        key={row.employee_id}
        depth={depth}
        testId={`organigrama-reporte-${row.employee_id}`}
        label={row.nombre_completo}
        meta={renderRowMeta(row)}
        defaultOpen={depth < 1}>
        {children.length > 0 && children.map((child) => renderRow(child, depth + 1, nextSeen))}
      </TreeNode>
    )
  }

  // `build_reporte` breaks cycles server-side; if none is left, a flat list beats a blank page.
  if (roots.length === 0) {
    return (
      <div data-testid="organigrama-reporte">
        <p className="mb-3 text-sm text-tone-amber-fg">{t('business.organigrama.cicloDetectado')}</p>
        <div data-testid="organigrama-reporte-plana">
          {reporte.map((row) => (
            <TreeNode
              key={row.employee_id}
              depth={0}
              testId={`organigrama-reporte-${row.employee_id}`}
              label={row.nombre_completo}
              meta={renderRowMeta(row)}
            />
          ))}
        </div>
      </div>
    )
  }

  return <div data-testid="organigrama-reporte">{roots.map((row) => renderRow(row, 0, new Set()))}</div>
}

export function OrganigramaSection({businessId, canEdit}: {businessId: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const {data, isLoading} = useOrganigrama(businessId)
  const [view, setView] = useState<ViewKey>('estructura')
  const [reporteModo, setReporteModo] = useState<ReporteModo>('lista')

  // No h2: the Organigrama page's PageHeading already titles it (UX O1).
  return (
    <section className="surface" data-testid="perfil-organigrama-section">
      <div className="surface-divider flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
        <SegmentedControl
          value={view}
          onChange={setView}
          options={(['estructura', 'reporte'] as const).map((key) => ({
            key,
            label: t(`business.organigrama.view.${key}`),
            testId: `organigrama-view-${key}`,
          }))}
        />
        {view === 'reporte' && (
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              size="sm"
              value={reporteModo}
              onChange={setReporteModo}
              options={(['lista', 'diagrama'] as const).map((key) => ({
                key,
                label: t(`business.organigrama.reporteModo.${key}`),
                testId: `organigrama-reporte-modo-${key}`,
              }))}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="organigrama-export-xlsx"
              onClick={() => downloadBlob(organigramaExportUrl(businessId), 'organigrama.xlsx')}>
              {t('business.organigrama.exportXlsx')}
            </Button>
          </div>
        )}
      </div>
      <div className="p-5">
        {isLoading || !data ? (
          <Skeleton className="h-40 w-full" />
        ) : view === 'estructura' ? (
          <EstructuraView businessId={businessId} estructura={data.estructura} canEdit={canEdit} />
        ) : reporteModo === 'lista' ? (
          <ReporteView businessId={businessId} reporte={data.reporte} canEdit={canEdit} />
        ) : (
          <OrganigramaDiagrama reporte={data.reporte} />
        )}
      </div>
    </section>
  )
}
